/**
 * 验证码辅助工具
 * 统一管理验证码相关的状态和逻辑
 * 用于登录页（login.vue）和 ContactUs.vue 等组件
 */

/**
 * 验证码状态管理类
 */
export class CaptchaState {
  constructor() {
    this.loading = false;
    this.submitting = false;
    this.captchaData = null;
    this.captchaVerified = false;
    this.captchaCallback = null;
    this.captchaTimeout = null;
  }

  /**
   * 重置验证码状态
   */
  reset() {
    this.loading = false;
    this.submitting = false;
    this.captchaData = null;
    this.captchaVerified = false;
    this.captchaCallback = null;
    if (this.captchaTimeout) {
      clearTimeout(this.captchaTimeout);
      this.captchaTimeout = null;
    }
  }

  /**
   * 设置加载状态
   * @param {boolean} loading - 是否加载中
   */
  setLoading(loading) {
    this.loading = loading;
  }

  /**
   * 设置提交状态
   * @param {boolean} submitting - 是否提交中
   */
  setSubmitting(submitting) {
    this.submitting = submitting;
  }

  /**
   * 设置验证码数据
   * @param {Object} data - 验证码数据
   */
  setCaptchaData(data) {
    this.captchaData = data;
    this.captchaVerified = !!data;
  }
}

/**
 * 显示 Geetest 验证码
 * @param {Object} context - Vue 组件实例
 * @param {string} refName - GeetestCaptcha 组件的 ref 名称
 * @param {Function} onVerified - 验证成功后的回调函数
 * @param {Function} onError - 错误回调函数
 */
export function showGeetestCaptcha(context, refName, onVerified, onError) {
  if (!context || !refName) {
    if (onError) {
      onError('验证码组件未初始化');
    }
    return;
  }

  // 设置回调
  if (context.captchaState) {
    context.captchaState.captchaCallback = onVerified;
  }

  context.$nextTick(() => {
    const captchaInstance = context.$refs[refName];
    
    if (!captchaInstance) {
      if (onError) {
        onError('验证码组件未找到');
      }
      if (context.captchaState) {
        context.captchaState.setLoading(false);
        context.captchaState.setSubmitting(false);
      }
      return;
    }

    try {
      if (typeof window !== 'undefined' && window.initGeetest4) {
        captchaInstance.showGeetest();
      } else {
        if (onError) {
          onError('验证码服务未加载，请刷新页面后重试');
        }
        if (context.captchaState) {
          context.captchaState.setLoading(false);
          context.captchaState.setSubmitting(false);
        }
      }
    } catch (error) {
      if (onError) {
        onError('验证码加载失败，请刷新页面后重试');
      }
      if (context.captchaState) {
        context.captchaState.setLoading(false);
        context.captchaState.setSubmitting(false);
      }
    }
  });
}

/**
 * 处理验证码验证成功回调
 * @param {Object} context - Vue 组件实例
 * @param {Object} captchaVerifyParam - 验证码验证参数
 * @param {Function} resolve - Promise resolve 函数（可选）
 * @returns {boolean} 验证是否成功
 */
export function handleCaptchaVerify(context, captchaVerifyParam, resolve) {
  // 检查验证码数据是否完整
  if (!captchaVerifyParam || 
      !captchaVerifyParam.lot_number || 
      !captchaVerifyParam.captcha_output || 
      !captchaVerifyParam.pass_token) {
    if (context.captchaState) {
      context.captchaState.setLoading(false);
      context.captchaState.setSubmitting(false);
    }
    if (resolve) {
      resolve(false);
    }
    return false;
  }

  // 更新验证码状态
  if (context.captchaState) {
    context.captchaState.setCaptchaData(captchaVerifyParam);
  }

  // 执行回调
  if (context.captchaState && context.captchaState.captchaCallback) {
    context.captchaState.captchaCallback();
    context.captchaState.captchaCallback = null;
  }

  if (resolve) {
    resolve({ success: true });
  }

  return true;
}

/**
 * 创建验证码 Mixin
 * 用于在组件中快速集成验证码功能
 */
export const captchaMixin = {
  data() {
    return {
      captchaState: new CaptchaState()
    };
  },
  methods: {
    /**
     * 显示验证码
     * @param {string} refName - GeetestCaptcha 组件的 ref 名称
     * @param {Function} onVerified - 验证成功后的回调函数
     */
    showCaptcha(refName, onVerified) {
      showGeetestCaptcha(
        this,
        refName,
        onVerified,
        (error) => {
          alert(error);
          this.captchaState.setLoading(false);
          this.captchaState.setSubmitting(false);
        }
      );
    },

    /**
     * 处理验证码验证成功回调
     * @param {Object} captchaVerifyParam - 验证码验证参数
     * @param {Function} resolve - Promise resolve 函数（可选）
     */
    handleCaptchaVerify(captchaVerifyParam, resolve) {
      return handleCaptchaVerify(this, captchaVerifyParam, resolve);
    },

    /**
     * 重置验证码状态
     */
    resetCaptchaState() {
      if (this.captchaState) {
        this.captchaState.reset();
      }
    }
  },
  beforeDestroy() {
    // 组件销毁时清理验证码状态
    if (this.captchaState) {
      this.captchaState.reset();
    }
  }
};

